import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { pmService } from '../../services/pmService';

const initialState = {
  maintenancePlans: [],
  maintenancePlan: null,
  maintenanceSchedules: [],
  maintenanceSchedule: null,
  breakdownReports: [],
  breakdownReport: null,
  emergencyWorkOrders: [],
  emergencyWorkOrder: null,
  equipmentHealth: [],
  anomalies: [],
  failurePredictions: [],
  equipmentSpecifications: [],
  equipmentParameters: [],
  conditionReadings: [],
  maintenanceTasks: [],
  taskExecutions: [],
  maintenanceResources: [],
  workOrderLabor: [],
  workOrderParts: [],
  defectAnalyses: [],
  failureHistory: [],
  predictiveModels: [],
  isLoading: false,
  error: null,
};

const makeThunk = (name, serviceCall) =>
  createAsyncThunk(`pm/${name}`, async (params = {}, { rejectWithValue }) => {
    try {
      const response = await serviceCall(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || `Error fetching ${name}`);
    }
  });

export const fetchMaintenancePlans = makeThunk('fetchMaintenancePlans', pmService.getMaintenancePlans);
export const fetchMaintenanceSchedules = makeThunk('fetchMaintenanceSchedules', pmService.getMaintenanceSchedules);
export const fetchBreakdownReports = makeThunk('fetchBreakdownReports', pmService.getBreakdownReports);
export const fetchEmergencyWorkOrders = makeThunk('fetchEmergencyWorkOrders', pmService.getEmergencyWorkOrders);
export const fetchEquipmentHealth = makeThunk('fetchEquipmentHealth', pmService.getEquipmentHealth);
export const fetchAnomalies = makeThunk('fetchAnomalies', pmService.getAnomalies);
export const fetchFailurePredictions = makeThunk('fetchFailurePredictions', pmService.getFailurePredictions);
export const fetchEquipmentSpecifications = makeThunk('fetchEquipmentSpecifications', pmService.getEquipmentSpecifications);
export const fetchEquipmentParameters = makeThunk('fetchEquipmentParameters', pmService.getEquipmentParameters);
export const fetchConditionReadings = makeThunk('fetchConditionReadings', pmService.getConditionReadings);
export const fetchMaintenanceTasks = makeThunk('fetchMaintenanceTasks', pmService.getMaintenanceTasks);
export const fetchTaskExecutions = makeThunk('fetchTaskExecutions', pmService.getTaskExecutions);
export const fetchMaintenanceResources = makeThunk('fetchMaintenanceResources', pmService.getMaintenanceResources);
export const fetchWorkOrderLabor = makeThunk('fetchWorkOrderLabor', pmService.getWorkOrderLabor);
export const fetchWorkOrderParts = makeThunk('fetchWorkOrderParts', pmService.getWorkOrderParts);
export const fetchDefectAnalyses = makeThunk('fetchDefectAnalyses', pmService.getDefectAnalyses);
export const fetchFailureHistory = makeThunk('fetchFailureHistory', pmService.getFailureHistory);
export const fetchPredictiveModels = makeThunk('fetchPredictiveModels', pmService.getPredictiveModels);

export const fetchMaintenancePlan = createAsyncThunk(
  'pm/fetchMaintenancePlan',
  async (id, { rejectWithValue }) => {
    try {
      const response = await pmService.getMaintenancePlan(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching maintenance plan');
    }
  }
);

export const createMaintenancePlan = createAsyncThunk(
  'pm/createMaintenancePlan',
  async (data, { rejectWithValue }) => {
    try {
      const response = await pmService.createMaintenancePlan(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error creating maintenance plan');
    }
  }
);

export const updateMaintenancePlan = createAsyncThunk(
  'pm/updateMaintenancePlan',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await pmService.updateMaintenancePlan(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error updating maintenance plan');
    }
  }
);

const addListCases = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => { state.isLoading = true; state.error = null; })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state[stateKey] = action.payload.results || action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
};

const pmSlice = createSlice({
  name: 'pm',
  initialState,
  reducers: {
    clearPmError: (state) => { state.error = null; },
    clearMaintenancePlan: (state) => { state.maintenancePlan = null; },
  },
  extraReducers: (builder) => {
    addListCases(builder, fetchMaintenancePlans, 'maintenancePlans');
    addListCases(builder, fetchMaintenanceSchedules, 'maintenanceSchedules');
    addListCases(builder, fetchBreakdownReports, 'breakdownReports');
    addListCases(builder, fetchEmergencyWorkOrders, 'emergencyWorkOrders');
    addListCases(builder, fetchEquipmentHealth, 'equipmentHealth');
    addListCases(builder, fetchAnomalies, 'anomalies');
    addListCases(builder, fetchFailurePredictions, 'failurePredictions');
    addListCases(builder, fetchEquipmentSpecifications, 'equipmentSpecifications');
    addListCases(builder, fetchEquipmentParameters, 'equipmentParameters');
    addListCases(builder, fetchConditionReadings, 'conditionReadings');
    addListCases(builder, fetchMaintenanceTasks, 'maintenanceTasks');
    addListCases(builder, fetchTaskExecutions, 'taskExecutions');
    addListCases(builder, fetchMaintenanceResources, 'maintenanceResources');
    addListCases(builder, fetchWorkOrderLabor, 'workOrderLabor');
    addListCases(builder, fetchWorkOrderParts, 'workOrderParts');
    addListCases(builder, fetchDefectAnalyses, 'defectAnalyses');
    addListCases(builder, fetchFailureHistory, 'failureHistory');
    addListCases(builder, fetchPredictiveModels, 'predictiveModels');

    builder
      .addCase(fetchMaintenancePlan.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchMaintenancePlan.fulfilled, (state, action) => { state.isLoading = false; state.maintenancePlan = action.payload; })
      .addCase(fetchMaintenancePlan.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(createMaintenancePlan.fulfilled, (state, action) => { state.maintenancePlans.push(action.payload); })
      .addCase(updateMaintenancePlan.fulfilled, (state, action) => {
        const index = state.maintenancePlans.findIndex(p => p.id === action.payload.id);
        if (index !== -1) state.maintenancePlans[index] = action.payload;
      });
  },
});

export const { clearPmError, clearMaintenancePlan } = pmSlice.actions;
export default pmSlice.reducer;
